import { useEffect, useRef, useState } from 'react';
import type { PieceSet } from '@tariff/types';
import { priceStay } from '@tariff/engine';
import { useT } from '../lib/i18n';
import { shortDate, money } from '../lib/format';

const LENGTHS = [1, 2, 3, 4, 5, 7, 10, 14, 21];
const ROWS = 21;
const RAMP = ['--q1', '--q2', '--q3', '--q4', '--q5'];

interface Cell { arrival: number; los: number; total: number | null }

/**
 * What a number in this grid means.
 *
 * `total` first, because it is the question an operator is actually asking —
 * what does a stay of this length cost — and the grid used to answer only the
 * third one. The other two are readings OF the total, not different prices:
 *
 *   total     the price of the whole stay
 *   extra     what the nights past the previous length cost, each. The
 *             MARGINAL rate, which is where a park's own reasoning lives: at
 *             De Vergarde nights four to seven are about EUR 29 each and
 *             nights eight onward about EUR 50.
 *   perNight  the total divided by the nights, which is the AVERAGE — and the
 *             only one of the three a channel ever sees.
 *
 * Keeping all three a click apart is the point. The marginal and the average
 * disagree completely on this park's rentals, and an operator reading one while
 * a channel reads the other is how "the price goes up" and "the price only ever
 * falls" end up both being true.
 */
type View = 'total' | 'extra' | 'perNight';
interface Computed { start: number; cells: Cell[] }

/**
 * Price per night across arrivals x lengths.
 *
 * Sequential encoding — one hue, light to dark, low values receding toward the
 * surface — because the quantity is magnitude, not identity. Unavailable cells
 * are hatched rather than coloured, so "no price" never reads as "cheap".
 *
 * Computed in slices via requestAnimationFrame. A camping programme carries 114
 * rates and each cell is a full search, so a synchronous pass freezes the tab.
 */
export function PriceGrid({ set, arrival, nights, quantities, onPick }: {
  set: PieceSet;
  arrival: number;
  nights: number;
  quantities: Record<string, number>;
  onPick: (arrival: number, nights: number) => void;
}) {
  const t = useT();
  // The computed grid carries the `start` it was computed FOR. Clicking a cell
  // changes the arrival, which re-renders with a new `start` one frame before
  // the effect rebuilds the cells — and looking up the new coordinates in the
  // old array returned undefined and threw. Keeping them together makes the
  // stale frame representable instead of a crash.
  const [view, setView] = useState<View>('total');
  const [computed, setComputed] = useState<Computed | null>(null);
  const [progress, setProgress] = useState(0);
  const token = useRef(0);
  const start = arrival - 7;

  useEffect(() => {
    const mine = ++token.current;
    const work: Cell[] = [];
    for (let r = 0; r < ROWS; r++) {
      for (const los of LENGTHS) work.push({ arrival: start + r, los, total: null });
    }
    setComputed(null);
    setProgress(0);

    let i = 0;
    const step = () => {
      if (mine !== token.current) return;
      const t0 = performance.now();
      while (i < work.length && performance.now() - t0 < 12) {
        const c = work[i];
        try {
          const q = priceStay(set, { arrival: c.arrival, nights: c.los, quantities });
          c.total = q.status === 'unavailable' ? null : q.total;
        } catch {
          c.total = null;
        }
        i++;
      }
      setProgress(i / work.length);
      if (i < work.length) { requestAnimationFrame(step); return; }
      setComputed({ start, cells: work });
    };
    const h = requestAnimationFrame(step);
    return () => { token.current++; cancelAnimationFrame(h); };
  }, [set, start, quantities]);

  const cells = computed && computed.start === start ? computed.cells : null;

  /**
   * The number this cell shows, under the current reading.
   *
   * `extra` needs the length BELOW it on the same arrival, so it is derived
   * here rather than stored: the same computed grid answers all three, and
   * switching view must not re-price 189 stays.
   */
  const valueOf = (c: Cell): number | null => {
    if (c.total === null) return null;
    if (view === 'total') return c.total;
    if (view === 'perNight') return c.total / c.los;
    const i = LENGTHS.indexOf(c.los);
    // The shortest length has nothing before it, so its extra IS its total.
    if (i <= 0) return c.total / c.los;
    const prev = cells?.find((x) => x.arrival === c.arrival && x.los === LENGTHS[i - 1]);
    if (!prev || prev.total === null) return null;
    return (c.total - prev.total) / (c.los - LENGTHS[i - 1]!);
  };

  const values = (cells ?? []).map(valueOf).filter((v): v is number => v !== null);
  const lo = values.length ? Math.min(...values) : 0;
  const hi = values.length ? Math.max(...values) : 1;

  return (
    <>
      {/* Three readings of one grid, not three grids. Switching re-reads the
          cells already priced. */}
      <div className="tabs" role="tablist" style={{ marginBottom: 10 }}>
        {(['total', 'extra', 'perNight'] as const).map((v) => (
          <button
            key={v}
            type="button"
            role="tab"
            className="tab"
            aria-selected={view === v}
            onClick={() => setView(v)}
          >
            {t(`grid.view.${v}` as 'grid.view.total')}
          </button>
        ))}
        <span className="grow" />
        <span className="note">{t(`grid.viewNote.${view}` as 'grid.viewNote.total')}</span>
      </div>
      {!cells && (
        <div style={{ padding: '8px 0 14px' }}>
          <div className="bar-track"><div className="bar-fill" style={{ width: `${progress * 100}%` }} /></div>
          <p className="note" style={{ marginTop: 6 }}>
            {t('grid.pricing', { count: ROWS * LENGTHS.length })}
          </p>
        </div>
      )}
      {cells && (
        <div className="hm">
          <table>
            <thead>
              <tr>
                <th aria-label={t('grid.arrivalColumn')} />
                {LENGTHS.map((n) => <th key={n}>{t('grid.losHeader', { nights: n })}</th>)}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: ROWS }, (_, r) => {
                const a = start + r;
                return (
                  <tr key={a}>
                    <td className="rowh">{shortDate(a)}</td>
                    {LENGTHS.map((los) => {
                      const c = cells.find((x) => x.arrival === a && x.los === los);
                      const sel = a === arrival && los === nights;
                      const shown = c ? valueOf(c) : null;
                      if (!c || shown === null) {
                        return (
                          <td className="cell" key={los}>
                            <button
                              type="button"
                              className={`cb na${sel ? ' sel' : ''}`}
                              onClick={() => onPick(a, los)}
                              title={c
                                ? t('grid.cellUnavailable', { date: shortDate(a), nights: los })
                                : t('grid.stillPricing')}
                            >—</button>
                          </td>
                        );
                      }
                      const position = hi > lo ? (shown - lo) / (hi - lo) : 0.5;
                      const k = Math.min(RAMP.length - 1, Math.floor(position * RAMP.length));
                      return (
                        <td className="cell" key={los}>
                          <button
                            type="button"
                            className={`cb${sel ? ' sel' : ''}`}
                            onClick={() => onPick(a, los)}
                            style={{
                              background: `var(${RAMP[k]})`,
                              color: k >= 3 ? 'var(--q-ink-hi)' : 'var(--q-ink-lo)',
                            }}
                            title={t('grid.cellPrice', {
                              date: shortDate(a), nights: los, price: money(shown),
                            })}
                          >
                            {Math.round(shown)}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div className="scale">
        <span>{t('grid.cheaper')}</span>
        <span className="bar">
          {RAMP.map((v) => <i key={v} style={{ background: `var(${v})` }} />)}
        </span>
        <span>{t('grid.dearer')}</span>
      </div>
    </>
  );
}
